import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import Pre from "@ui/pre/Pre";
import { Eye } from "lucide-react";
import { notify } from "src/tools/notify";
import styled from "styled-components";

type THtmlFragment = { path: string; html: string };

export const getDectaHtmlFragments = (data: unknown, path = ""): THtmlFragment[] => {
	if (typeof data === "string") {
		return /^\s*(?:<!doctype\s+html\b|<html\b|<form\b|<iframe\b)/i.test(data)
			? [{ path: path || "HTML", html: data }]
			: [];
	}
	if (!data || typeof data !== "object") return [];
	return Object.entries(data).flatMap(([key, value]) =>
		getDectaHtmlFragments(value, Array.isArray(data) ? `${path}[${key}]` : path ? `${path}.${key}` : key),
	);
};

const Payload = styled.div`
	min-width: 0;
	border: 1px solid #56b6c255;
	border-radius: 8px;
	overflow: hidden;
	background: rgb(34 36 47);
`;

const PreviewActions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 8px 10px;
	background: #56b6c21a;
	border-bottom: 1px solid #56b6c233;
`;

const PreviewAction = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
	color: #bfe9ed;
	code {
		flex: 1;
		min-width: 0;
		font: 12px/1.5 Consolas, monospace;
		overflow-wrap: anywhere;
	}
`;

const PreviewButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	flex-shrink: 0;
	min-height: 32px;
	padding: 6px 9px;
	border: 1px solid #56b6c266;
	border-radius: 5px;
	background: #56b6c21a;
	color: inherit;
	font: inherit;
	font-size: 12px;
	cursor: pointer;
	&:hover { background: #56b6c233; }
	&:focus-visible { outline: 2px solid #56b6c2; outline-offset: 2px; }
`;

export const openDectaHtmlPreview = (fragment: THtmlFragment) => {
	const screen = window.screen as Screen & { availLeft?: number; availTop?: number };
	const margin = 16;
	const frameWidth = Math.max(16, window.outerWidth - window.innerWidth);
	const frameHeight = Math.max(100, window.outerHeight - window.innerHeight);
	const width = Math.max(100, Math.min(900, screen.availWidth - margin * 2 - frameWidth));
	const height = Math.max(100, Math.min(760, screen.availHeight - margin * 2 - frameHeight));
	const minLeft = (screen.availLeft ?? 0) + margin;
	const minTop = (screen.availTop ?? 0) + margin;
	const left = Math.round(Math.max(minLeft, Math.min(
		window.screenX + (window.outerWidth - width - frameWidth) / 2,
		minLeft + screen.availWidth - margin * 2 - width - frameWidth,
	)));
	const top = Math.round(Math.max(minTop, Math.min(
		window.screenY + (window.outerHeight - height - frameHeight) / 2,
		minTop + screen.availHeight - margin * 2 - height - frameHeight,
	)));
	const preview = window.open(
		"about:blank",
		"_blank",
		`popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
	);
	if (!preview) {
		notify.error(msg("thePreviewWindowWasBlockedAllowPopUpsForThisSite"));
		return;
	}

	preview.opener = null;
	preview.document.title = msg("templateHtmlPreview_3103747d", { v0: fragment.path });
	const viewport = preview.document.createElement("meta");
	viewport.name = "viewport";
	viewport.content = "width=device-width, initial-scale=1";
	preview.document.head.appendChild(viewport);
	preview.document.body.style.margin = "0";
	const iframe = preview.document.createElement("iframe");
	iframe.title = msg("templateHtmlPreview_3103747d", { v0: fragment.path });
	iframe.setAttribute("sandbox", "allow-scripts allow-forms");
	iframe.referrerPolicy = "no-referrer";
	iframe.srcdoc = fragment.html;
	iframe.style.cssText = "display:block;width:100%;height:100dvh;border:0;background:#fff";
	preview.document.body.replaceChildren(iframe);
	preview.focus();
};

export const DectaPayloadPreview = ({ data }: { data: unknown }) => {
	useLocale();
	const fragments = getDectaHtmlFragments(data);
	if (!fragments.length) return <Pre data={data} wrap />;

	return (
		<Payload>
			<PreviewActions>
				{fragments.map(fragment => (
					<PreviewAction key={fragment.path}>
						<code>{fragment.path}</code>
						<PreviewButton type="button" title={msg("templatePreview_02480053", { v0: fragment.path })} aria-label={msg("templatePreview_02480053", { v0: fragment.path })} onClick={() => openDectaHtmlPreview(fragment)}>
							<Eye size={15} aria-hidden="true" />{msg("preview")}</PreviewButton>
					</PreviewAction>
				))}
			</PreviewActions>
			<Pre data={data} wrap />
		</Payload>
	);
};
